/** Shared plan-step parsing and CoT visibility helpers (web agent UI). */

export interface AgentStepLike {
  thought?: string
  tool?: string | null
  toolOutput?: unknown
  status?: string
}

export const isNonActionablePlanStep = (text: string): boolean => {
  const t = text.toLowerCase().trim()
  if (!t) return true
  return (
    /\backnowledge\b/.test(t)
    || /\bconfirm\b.*\b(responsive|working|online|alive|receiv)/.test(t)
    || /\blet the user know\b/.test(t)
    || /\binform the user\b/.test(t)
    || /\breply to (the )?(user|message|text)\b/.test(t)
    || /\brespond to (the )?(user|message|greeting)\b/.test(t)
    || (/\b(greet|greeting)\b/.test(t) && /\buser\b/.test(t))
    || /\bcheck (that )?(the )?(system|agent) is (responsive|working|online|alive)\b/.test(t)
  )
}

export const cleanPlanItem = (text: string): string => {
  return text
    .replace(/\\\*/g, '*')
    .replace(/\*\*/g, '')
    .replace(/^\s*[-•*]\s+/, '')
    .trim()
}

/** Keep in sync with AgentChainOfThought isPlanText / papaya isPlanLikeText. */
export const isPlanText = (text: string): boolean => {
  if (!text || typeof text !== 'string') return false
  const t = text.trim()
  return (
    /^📋\s*\*{0,2}(?:Analysis\s+)?Plan\b/im.test(t)
    || /^\*{0,2}📋\s*\*{0,2}(?:Analysis\s+)?Plan\b/im.test(t)
    || /^EXECUTION PLAN\b/im.test(t)
    || /^(?:\*\*)?Analysis Plan(?:\*\*)?\b/im.test(t)
    || /^plan:\s/i.test(t)
    || /^\*\*plan:\*\*\s/i.test(t)
    || text.includes('📋 Plan:')
    || text.includes('**Plan:**')
    || text.includes('📋 **Plan')
    || text.includes('**📋 Plan')
  )
}

export const extractPlanItems = (text: string): string[] => {
  const items: string[] = []
  for (const line of text.split(/\n/)) {
    const trimmed = line.trim()
    const numbered = trimmed.match(/^\d+[\.)]\s*(.+)/)
    if (numbered) {
      const item = cleanPlanItem(numbered[1].trim())
      if (!isNonActionablePlanStep(item)) items.push(item)
      continue
    }
    const stepLine = trimmed.match(/^Step\s+\d+:\s*(.+)/i)
    if (stepLine) {
      const item = cleanPlanItem(stepLine[1].trim())
      if (!isNonActionablePlanStep(item)) items.push(item)
    }
  }
  return items
}

const stripMcpPrefix = (toolName: string): string =>
  toolName.replace(/^(mcp__payhelm__|mcp__custom__|payhelm__)/, '')

const UNPREFIXED_SUBSTANTIVE_TOOLS = new Set(['image_analysis'])

/**
 * Any MCP-server tool counts as real work. Matching on the `mcp__` prefix rather
 * than the payhelm/custom servers alone keeps knowledge- and PDF-server calls
 * visible; a run built only from those used to render no chain of thought at all.
 */
const isMcpTool = (toolName: string): boolean => {
  if (!toolName) return false
  if (UNPREFIXED_SUBSTANTIVE_TOOLS.has(toolName.toLowerCase())) return true
  return toolName.startsWith('mcp__') || toolName.startsWith('payhelm__')
}

/** Chain-of-thought header the Deep Dive lead brief is streamed under (papaya deepDive.ts). */
const DEEP_DIVE_BRIEF_MARKER = 'Deep Dive Brief'

const isPlanTool = (tool: string): boolean => {
  const name = stripMcpPrefix(tool)
  return name === 'create_analysis_plan' || name === 'create_plan'
}

const planTextFromToolOutput = (output: unknown): string => {
  if (output == null) return ''
  if (typeof output === 'string') return output
  if (typeof output === 'object' && output !== null) {
    const o = output as Record<string, unknown>
    if (typeof o.text === 'string') return o.text
    if (typeof o.content === 'string') return o.content
    if (Array.isArray(o.content)) {
      return o.content
        .filter((b: unknown) => typeof b === 'object' && b !== null && (b as { type?: string }).type === 'text')
        .map((b: unknown) => String((b as { text?: string }).text ?? ''))
        .join('\n')
    }
  }
  try {
    return JSON.stringify(output)
  } catch {
    return String(output)
  }
}

/** True when the turn has real work to show (tools, actionable plan, C-suite, etc.). */
export const hasSubstantiveAgentSteps = (steps: AgentStepLike[] | undefined | null): boolean => {
  if (!steps?.length) return false

  for (const step of steps) {
    if (step.tool && isMcpTool(step.tool)) return true

    if (step.thought?.startsWith('csuite:')) return true

    const thought = step.thought?.trim() || ''
    // The lead brief directs the whole Deep Dive, so it is worth showing on its
    // own — including when the run stopped before any tool came back.
    if (thought.includes(DEEP_DIVE_BRIEF_MARKER)) return true
    if (thought && isPlanText(thought) && extractPlanItems(thought).length > 0) return true

    if (step.tool && isPlanTool(step.tool)) {
      const raw = planTextFromToolOutput(step.toolOutput ?? step.thought)
      if (extractPlanItems(raw).length > 0) return true
    }
  }

  return false
}
